import { useMemo, useRef } from 'react';

import { cn } from '@shared/lib/cn';
import { formatNumber, formatPercent } from '@shared/lib/format';

import {
  CHART_DEFAULT_WIDTH,
  CHART_SIZE,
  CHART_SIZE_CLASS,
  NEGATIVE_BG_CLASS,
  POSITIVE_BG_CLASS,
  TREEMAP_LAYOUT,
} from './chart.constant';
import { estimateTextWidth } from './chart.lib';
import { CHART_TEXT } from './chart.text';
import { useElementWidth } from './chart-size.hook';
import { useChartTooltip } from './chart-tooltip.hook';
import { ChartTooltip } from './chart-tooltip.ui';
import type { ChartTooltipContent } from './model/chart-tooltip.types';
import type { TreemapGroup, TreemapItem } from './model/treemap.types';
import { treemapLayout, treemapTextLevel } from './treemap.lib';

export type TreemapStatus = 'default' | 'empty';

export interface TreemapProps {
  /** Figma `status`. empty = 모든 구간이 비었을 때의 안내 */
  status?: TreemapStatus;
  groups: TreemapGroup[];
  tooltip?: (item: TreemapItem, group: TreemapGroup) => ChartTooltipContent;
  /** status=empty 의 문구. 기본 "유효한 데이터가 없습니다." */
  emptyText?: string;
  className?: string;
}

/** 구간 단계 5 이하는 옅은 면 → 어두운 글자, 6 이상은 흰 글자(규격 §3) */
const LIGHT_MAX_STEP = 5;

/** 말풍선 기본 내용: 제목 = 구간 이름, 줄 = "회사 [값]" · 크기 */
const defaultTooltip = (item: TreemapItem, group: TreemapGroup): ChartTooltipContent => ({
  title: group.label,
  rows: [{ name: `${item.label} [${item.valueText}]`, value: formatNumber(item.size) }],
});

/**
 * 트리맵(네모 넓이 = 크기 값, 색 = 구간). 배치는 d3-hierarchy squarify, 그리기는 직접(DD-54 #1).
 * 높이 664 고정 · 너비는 놓이는 자리를 채운다. 네모 사이 2px(`dataviz/gap`), 면 = `dataviz/positive|negative/<step>`.
 * 네모 글자는 가운데 — 단계는 TREEMAP_TEXT_LEVELS(기본: 두 줄 heading-3 → 두 줄 micro → 이름만 한 줄 말줄임 → 없음).
 * 구간 이름표 "(비율) 구간" 은 구간 왼쪽 위(말풍선 면 · caption-strong). 네모에 올리면 ChartTooltip.
 * 그릴 항목이 없거나 status=empty 면 안내 글자(높이 400, 가운데 body 14).
 */
export function Treemap({
  status = 'default',
  groups,
  tooltip: tooltipOf = defaultTooltip,
  emptyText = CHART_TEXT.treemapEmpty,
  className,
}: TreemapProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const width = useElementWidth(rootRef, CHART_DEFAULT_WIDTH);
  const height = CHART_SIZE.treemapHeight;
  const { tooltip, show, hide, bubbleProps } = useChartTooltip();
  const layout = useMemo(() => treemapLayout(groups, width, height), [groups, width, height]);

  const empty =
    status === 'empty' ||
    !groups.some((group) =>
      group.items.some((item) => Number.isFinite(item.size) && item.size > 0),
    );

  return (
    <div
      ref={rootRef}
      role={empty ? undefined : 'img'}
      aria-label={
        empty ? undefined : CHART_TEXT.summary.treemap(layout.groups.length, layout.tiles.length)
      }
      data-chart="treemap"
      data-status={empty ? 'empty' : undefined}
      onPointerLeave={hide}
      className={cn(
        'relative w-full',
        empty && CHART_SIZE_CLASS.treemapEmpty,
        empty && 'flex items-center justify-center bg-bg-surface text-fg-default typo-body',
        !empty && CHART_SIZE_CLASS.treemap,
        !empty && 'overflow-hidden bg-dataviz-gap select-none',
        className,
      )}
    >
      {empty ? <p>{emptyText}</p> : null}
      {(empty ? [] : layout.tiles).map((tile) => {
        const { item, group } = tile;
        const key = `${tile.groupIndex}-${item.id}`;
        const level = treemapTextLevel(tile, item.label, item.valueText);
        const light = group.step <= LIGHT_MAX_STEP;
        return (
          <div
            key={key}
            data-slot="tile"
            data-step={group.step}
            data-tone={group.tone}
            data-text={level?.key ?? 'none'}
            style={{ left: tile.x, top: tile.y, width: tile.width, height: tile.height }}
            onPointerMove={(event) => {
              show({
                key,
                content: tooltipOf(item, group),
                anchor: { x: event.clientX, y: event.clientY },
              });
            }}
            className={cn(
              'absolute flex flex-col items-center justify-center overflow-hidden px-1 text-center transition hover:brightness-95',
              group.tone === 'positive'
                ? POSITIVE_BG_CLASS[group.step]
                : NEGATIVE_BG_CLASS[group.step],
              light ? 'text-dataviz-on-light' : 'text-dataviz-on-dark',
            )}
          >
            {level ? (
              <>
                <span className={cn('max-w-full truncate', level.className)}>{item.label}</span>
                {level.lines === 'both' ? (
                  <span className={cn('max-w-full truncate', level.className)}>
                    {item.valueText}
                  </span>
                ) : null}
              </>
            ) : null}
          </div>
        );
      })}
      {(empty ? [] : layout.groups).map((box) => {
        const tag = `(${box.share === null ? '-' : formatPercent(box.share, { maximumFractionDigits: 1 })}) ${box.group.label}`;
        const { tagHeight, tagOffset } = TREEMAP_LAYOUT;
        const fits =
          box.width >= estimateTextWidth(tag) + tagOffset * 4 &&
          box.height >= tagHeight + tagOffset * 2;
        return fits ? (
          <span
            key={`tag-${box.groupIndex}`}
            data-slot="group-tag"
            style={{ left: box.x + tagOffset, top: box.y + tagOffset }}
            className="pointer-events-none absolute rounded-sm bg-dataviz-tooltip px-1 whitespace-nowrap text-dataviz-on-tooltip typo-caption-strong"
          >
            {tag}
          </span>
        ) : null;
      })}
      {tooltip ? (
        <ChartTooltip
          {...tooltip.content}
          anchor={tooltip.anchor}
          placement={tooltip.placement}
          {...bubbleProps}
        />
      ) : null}
    </div>
  );
}
